import { useState } from 'react';
import { ChevronDown, ChevronUp, User, Clock, Car } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import type { VisitorPass } from '@condo/contracts';
import { QrDisplay } from './qr-display';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils/cn';

function statusBadge(status: VisitorPass['status']) {
  switch (status) {
    case 'pending':
      return <Badge variant="warning">Pendiente</Badge>;
    case 'used':
      return <Badge variant="success">Ingresado</Badge>;
    case 'expired':
      return <Badge variant="neutral">Expirado</Badge>;
  }
}

function formatDt(iso: string) {
  return format(new Date(iso), 'd MMM yyyy, HH:mm', { locale: es });
}

export function PassCard({ pass }: { pass: VisitorPass }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] transition-shadow hover:shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-4 p-4 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[var(--color-paper-dim)]">
            <User className="h-5 w-5 text-[var(--color-ink-muted)]" />
          </div>
          <div className="min-w-0">
            <p className="truncate font-medium text-[var(--color-ink)]">
              {pass.visitorNombre}
            </p>
            <div className="mt-0.5 flex items-center gap-2 text-[0.75rem] text-[var(--color-ink-muted)]">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDt(pass.createdAt)}
              </span>
              {pass.vehiclePatent && (
                <span className="flex items-center gap-1">
                  <Car className="h-3 w-3" />
                  {pass.vehiclePatent}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {statusBadge(pass.status)}
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-[var(--color-ink-muted)]" />
          ) : (
            <ChevronDown className="h-4 w-4 text-[var(--color-ink-muted)]" />
          )}
        </div>
      </button>
      <div
        className={cn(
          'overflow-hidden transition-all duration-200',
          expanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        <div className="border-t border-[var(--color-hairline)] p-4">
          <QrDisplay pass={pass} />
        </div>
      </div>
    </div>
  );
}
